import { useEffect, useMemo, useState } from 'react';

import { loadEvents } from '../api/events';
import { useAuth } from '../state/AuthProvider';
import { Shell } from '../ui/PwaContent';
import { CalendarEvent, HalfYear } from './calendarTypes';
import {
    getCurrentHalfYear,
    getDateKey,
    getDaysInMonth,
    getEventTimeLabel,
    getHalfYearMonths,
    getMonthLabel,
    getWeekdayLabel,
} from './calendarUtils';

export function CalendarPrintHalfYearView() {
    const authContext = useAuth();

    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [halfYear, setHalfYear] = useState<HalfYear>(() => getCurrentHalfYear());

    useEffect(() => {
        let cancelled = false;

        async function loadCalendarEvents() {
            setLoading(true);
            setError(null);

            try {
                const result = await loadEvents(authContext.apiBaseUrl);

                if (!cancelled) {
                    setEvents(result);
                }
            } catch (loadError) {
                if (!cancelled) {
                    setError(
                        loadError instanceof Error
                            ? loadError.message
                            : 'Termine konnten nicht geladen werden.',
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        void loadCalendarEvents();

        return () => {
            cancelled = true;
        };
    }, [authContext.apiBaseUrl]);

    const months = useMemo(() => getHalfYearMonths(halfYear), [halfYear]);

    const eventsByDate = useMemo(() => {
        const grouped = new Map<string, CalendarEvent[]>();

        for (const event of events) {
            grouped.set(event.date, [...(grouped.get(event.date) ?? []), event]);
        }

        return grouped;
    }, [events]);

    function setPreviousHalfYear() {
        setHalfYear((current) => {
            if (current.half === 2) {
                return { year: current.year, half: 1 };
            }

            return { year: current.year - 1, half: 2 };
        });
    }

    function setNextHalfYear() {
        setHalfYear((current) => {
            if (current.half === 1) {
                return { year: current.year, half: 2 };
            }

            return { year: current.year + 1, half: 1 };
        });
    }

    if (loading) {
        return (
            <Shell title="Halbjahreskalender" subtitle="Druckansicht wird geladen ..." />
        );
    }

    if (error) {
        return (
            <Shell title="Halbjahreskalender" subtitle="Fehler beim Laden">
                <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-900">
                    {error}
                </div>
            </Shell>
        );
    }

    return (
        <Shell
            title="Halbjahreskalender"
            subtitle={`${halfYear.half}. Halbjahr ${halfYear.year}`}
        >
            <div className="mb-5 flex flex-wrap gap-2 print:hidden">
                <button type="button" className="wwpwa-button-secondary" onClick={setPreviousHalfYear}>
                    Vorheriges Halbjahr
                </button>

                <button type="button" className="wwpwa-button-secondary" onClick={setNextHalfYear}>
                    Nächstes Halbjahr
                </button>

                <button type="button" className="wwpwa-button-primary" onClick={() => window.print()}>
                    Drucken / PDF speichern
                </button>

                <a
                    className="rounded-xl border border-gray-200 bg-white px-4 py-2 font-bold text-gray-700 hover:bg-gray-50"
                    href="/kalender/"
                >
                    Zur Agenda
                </a>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-gray-300 bg-white print:overflow-visible print:rounded-none print:border-gray-400">
                <div className="grid min-w-[92rem] grid-cols-[repeat(6,4rem_minmax(0,1fr))] gap-px bg-gray-300 p-px text-xs print:min-w-0 print:grid-cols-[repeat(6,2.1rem_minmax(0,1fr))] print:text-[8px]">
                    <div className="contents">
                        {months.map((month) => (
                            <div
                                key={month}
                                className="col-span-2 bg-red-50 px-3 py-2 text-center text-sm font-bold text-gray-950 print:px-1 print:py-1 print:text-[9px]"
                            >
                                {getMonthLabel(`${halfYear.year}-${String(month).padStart(2, '0')}`)}
                            </div>
                        ))}
                    </div>

                    {Array.from({ length: 31 }, (_, index) => index + 1).map((day) => (
                        <div key={day} className="contents">
                            {months.map((month) => {
                                const daysInMonth = getDaysInMonth(halfYear.year, month);

                                if (day > daysInMonth) {
                                    return (
                                        <div key={`${month}-${day}-empty`} className="contents">
                                            <div className="bg-gray-50 px-1 py-1" />
                                            <div className="bg-gray-50 px-1 py-1" />
                                        </div>
                                    );
                                }

                                const dateKey = getDateKey(halfYear.year, month, day);
                                const dayEvents = eventsByDate.get(dateKey) ?? [];
                                const weekday = getWeekdayLabel(halfYear.year, month, day);
                                const isWeekend = weekday === 'Sa.' || weekday === 'So.';

                                return (
                                    <div key={`${month}-${day}`} className="contents">
                                        <div className={isWeekend ? 'bg-orange-100 px-2 py-1 font-bold' : 'bg-white px-2 py-1 font-bold'}>
                                            <time dateTime={dateKey} className="flex justify-between gap-1">
                                                <span>{weekday.replace('.', '')}</span>
                                                <span>{day}</span>
                                            </time>
                                        </div>

                                        <div className={dayEvents.length > 0 ? 'bg-blue-50 px-2 py-1' : 'bg-white px-2 py-1'}>
                                            <div className="grid gap-1">
                                                {dayEvents.map((event) => (
                                                    <div key={event.id} className="break-words rounded bg-white/70 px-1 py-0.5 leading-snug">
                                                        <strong>{event.title}</strong>
                                                        {getEventTimeLabel(event) && (
                                                            <span> · {getEventTimeLabel(event)}</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </Shell>
    );
}
